/**
 * Village OS Deluxe — Main SPA entry.
 * Hash-based router, state management, 5-second poll with visibility API.
 */

import { vegasToday, dateLong, dayName, addDays, parseDate } from '../shared/dates';
import { GROUPS, BLOCKS, NUM_BLOCKS, CCI_SEATS, CCI_SHORT, RWG, PORDER } from '../shared/constants';
import { slotKey, pLabel } from '../shared/engine';
import type { GroupDef } from '../shared/constants';

// ─── Types ──────────────────────────────────────────────────────────────────

interface AppState {
  currentDay: string;
  view: 'scheduler' | 'rotation' | 'history' | 'details' | 'calloffs' | 'sheet';
  dayVersion: number;
  globalVersion: number;
  loading: boolean;
  toast: string;
  toastTimeout: number | null;
  mobilePane: 'roster' | 'posts' | 'selected';
  selectedOfficer: number | null;
  selectedPost: string | null;
  selectedCci: number | null;
  searchQuery: string;
  rosterTab: 'swing' | 'day' | 'grave';
}

/** Parsed schedule data ready for rendering. */
interface ParsedSchedule {
  generation: number;
  posted: boolean;
  /** officerId -> [groupIdx, postIdx] per block (4 entries). */
  assignments: Record<string, [number, number][]>;
  /** slotKey -> officerId per block. */
  occupancy: Record<string, (number | null)[]>;
}

// Group colors (must match CSS and constants)
const GROUP_COLORS = ['#2563EB', '#7C3AED', '#0F766E', '#C2410C', '#BE185D', '#1F1D3A'];

// ─── State ──────────────────────────────────────────────────────────────────

const state: AppState = {
  currentDay: vegasToday(),
  view: 'scheduler',
  dayVersion: 0,
  globalVersion: 0,
  loading: true,
  toast: '',
  toastTimeout: null,
  mobilePane: 'posts',
  selectedOfficer: null,
  selectedPost: null,
  selectedCci: null,
  searchQuery: '',
  rosterTab: 'swing',
};

// ─── API Client ─────────────────────────────────────────────────────────────

const api = {
  async get(path: string): Promise<any> {
    const res = await fetch('/api/' + path);
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },
  async post(path: string, body: any) {
    const res = await fetch('/api/' + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.status === 409) {
      showToast('Schedule changed \u2014 reloading');
      await loadDay(state.currentDay);
      throw new Error('stale');
    }
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },
  async patch(path: string, body: any) {
    const res = await fetch('/api/' + path, {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.status === 409) {
      showToast('Schedule changed \u2014 reloading');
      await loadDay(state.currentDay);
      throw new Error('stale');
    }
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },
  async del(path: string, body?: any) {
    const res = await fetch('/api/' + path, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json' },
      body: body ? JSON.stringify(body) : undefined,
    });
    if (res.status === 409) {
      showToast('Schedule changed \u2014 reloading');
      await loadDay(state.currentDay);
      throw new Error('stale');
    }
    if (!res.ok) throw new Error(await res.text());
    return res.json();
  },
};

// ─── Data Cache ─────────────────────────────────────────────────────────────

let dayData: any = null;
let parsed: ParsedSchedule | null = null;
let roster: any[] = [];
let rosterById: Record<number, any> = {};
let globalData: any = { settings: {}, events: [], eventOfficers: [], specials: [], rollNotes: [] };
let timeOffData: any[] = [];
let trainingData: any[] = [];

function officerName(id: number): string {
  return rosterById[id]?.name || `#${id}`;
}

function parseSchedule(scheduleRow: any): ParsedSchedule {
  const assignments: Record<string, [number, number][]> = scheduleRow.assignments
    ? (typeof scheduleRow.assignments === 'string' ? JSON.parse(scheduleRow.assignments) : scheduleRow.assignments)
    : {};

  // Build occupancy: for each slot, who's there at each block
  const occupancy: Record<string, (number | null)[]> = {};
  for (const [oid, blocks] of Object.entries(assignments)) {
    const officerId = parseInt(oid, 10);
    if (!Array.isArray(blocks)) continue;
    for (let b = 0; b < NUM_BLOCKS && b < blocks.length; b++) {
      const [g, p] = blocks[b];
      if (g < 0) continue;
      const k = g + ':' + p;
      if (!occupancy[k]) occupancy[k] = [null, null, null, null];
      occupancy[k][b] = officerId;
    }
  }

  return {
    generation: scheduleRow.generation || 0,
    posted: !!scheduleRow.posted,
    assignments,
    occupancy,
  };
}

async function loadDay(day: string) {
  state.loading = true;
  render();
  try {
    const [schedData, rosterData] = await Promise.all([
      api.get(`schedule?day=${day}`),
      api.get('roster'),
    ]);
    dayData = schedData;
    roster = rosterData.officers;
    rosterById = {};
    for (const o of roster) rosterById[o.id] = o;
    if (dayData.schedule) {
      parsed = parseSchedule(dayData.schedule);
    } else {
      parsed = null;
    }
    state.dayVersion = dayData.schedule?.version || 0;
    state.globalVersion = dayData.globalVersion;
    state.loading = false;
  } catch (e) {
    console.error('Load failed:', e);
    state.loading = false;
  }
  render();
}

async function loadGlobalData() {
  try {
    const [details, toData, trData, config] = await Promise.all([
      api.get(`details?day=${state.currentDay}`),
      api.get('timeoff'),
      api.get('training'),
      api.get('config'),
    ]);
    globalData = { ...details, settings: config.settings };
    timeOffData = toData.entries || [];
    trainingData = trData.pairs || [];
  } catch (e) {
    console.error('Global load failed:', e);
  }
}

// ─── Helpers ────────────────────────────────────────────────────────────────

function showToast(msg: string) {
  state.toast = msg;
  if (state.toastTimeout) clearTimeout(state.toastTimeout);
  state.toastTimeout = window.setTimeout(() => { state.toast = ''; render(); }, 4000);
  render();
}

function displayName(n: string): string {
  const p = n.split(', ');
  return (p[1] || '') + ' ' + (p[0] || '');
}

function shortName(n: string): string {
  const p = n.split(', ');
  return (p[1]?.[0] || '') + '. ' + (p[0] || '');
}

function initials(n: string): string {
  const p = n.split(', ');
  return ((p[1]?.[0] || '') + (p[0]?.[0] || '')).toUpperCase();
}

function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** Find which group an officer is assigned to (first block). */
function officerGroup(id: number): number {
  if (!parsed) return -1;
  const blocks = parsed.assignments[String(id)];
  if (!blocks || !blocks.length) return -1;
  return blocks[0][0];
}

/** Check if an officer is called off today. */
function isCalledOff(id: number): boolean {
  if (!dayData?.calloffs) return false;
  return dayData.calloffs.some((c: any) => c.officer_id === id);
}

/** Get closures set for quick lookup. */
function closureMap(): Record<string, { reason: string; notes: string | null }> {
  const map: Record<string, { reason: string; notes: string | null }> = {};
  if (!dayData?.closures) return map;
  for (const c of dayData.closures) {
    map[c.post_group + ':' + c.post_index] = { reason: c.reason, notes: c.notes };
  }
  return map;
}

function formatHalf(h: number): string {
  if (h >= 48) return '0000';
  const hr = Math.floor(h / 2);
  const mn = h % 2 ? '30' : '00';
  return (hr < 10 ? '0' : '') + hr + mn;
}

// ─── Polling ────────────────────────────────────────────────────────────────

let pollTimer: number | null = null;

function startPoll() {
  stopPoll();
  pollTimer = window.setInterval(async () => {
    try {
      const data = await api.get(
        `poll?day=${state.currentDay}&dayV=${state.dayVersion}&globalV=${state.globalVersion}`
      );
      if (data.changed) {
        await loadDay(state.currentDay);
        await loadGlobalData();
      }
    } catch (e) { /* silent */ }
  }, 5000);
}

function stopPoll() {
  if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
}

document.addEventListener('visibilitychange', () => {
  if (document.hidden) {
    stopPoll();
  } else {
    (async () => {
      try {
        const data = await api.get(
          `poll?day=${state.currentDay}&dayV=${state.dayVersion}&globalV=${state.globalVersion}`
        );
        if (data.changed) {
          await loadDay(state.currentDay);
          await loadGlobalData();
        }
      } catch (e) { /* silent */ }
      startPoll();
    })();
  }
});

// ─── Navigation ─────────────────────────────────────────────────────────────

function goDay(day: string) {
  state.currentDay = day;
  state.selectedOfficer = null;
  state.selectedPost = null;
  state.selectedCci = null;
  state.toast = '';
  loadDay(day);
}

function prevDay() { goDay(addDays(state.currentDay, -1)); }
function nextDay() { goDay(addDays(state.currentDay, 1)); }

function setView(v: AppState['view']) {
  state.view = v;
  state.toast = '';
  render();
}

function setMobilePane(p: AppState['mobilePane']) {
  state.mobilePane = p;
  render();
}

// ─── Render: Main ───────────────────────────────────────────────────────────

const app = document.getElementById('app')!;

function render() {
  const isPhone = window.innerWidth < 691;
  const isTablet = window.innerWidth >= 691 && window.innerWidth < 1280;
  const isDesktop = window.innerWidth >= 1280;

  app.innerHTML = `
    <div class="vapp ${isDesktop ? 'desktop' : isTablet ? 'tablet' : 'phone'}">
      ${isDesktop ? renderSideMenu() : ''}
      <div class="vmain">
        ${renderHeader()}
        ${renderTabs()}
        ${state.toast ? `<div class="vtoast">${esc(state.toast)}</div>` : ''}
        <div class="vcontent">
          ${state.loading ? '<div class="vloading">Loading\u2026</div>' : renderView()}
        </div>
      </div>
    </div>
  `;
  bindEvents();
}

function renderSideMenu(): string {
  const items: { view: string; icon: string; label: string }[] = [
    { view: 'scheduler', icon: '\u{1F4CB}', label: 'Scheduler' },
    { view: 'rotation', icon: '\u{1F504}', label: 'Rotation' },
    { view: 'history', icon: '\u{1F4C5}', label: 'History' },
    { view: 'details', icon: '\u{1F4DD}', label: 'Details' },
  ];
  return `<nav class="vsidemenu">${items.map(i =>
    `<a href="#" class="vsidemenu-item ${state.view === i.view ? 'active' : ''}" data-view="${i.view}">
      <span class="vsidemenu-icon">${i.icon}</span>
      <span class="vsidemenu-label">${i.label}</span>
    </a>`
  ).join('')}</nav>`;
}

function renderHeader(): string {
  const day = state.currentDay;
  const titles: Record<string, string> = {
    scheduler: 'Swing Shift Scheduler',
    rotation: 'Rotation \u00B7 ' + day.substring(0, 7),
    history: 'Scheduler History',
    details: 'Details & Dispatch',
    calloffs: 'Call-offs & Requests',
    sheet: 'Full Schedule',
  };
  const statusBadge = parsed
    ? (parsed.posted
      ? '<span class="vbadge vbadge-posted">Published</span>'
      : parsed.generation > 0
        ? '<span class="vbadge vbadge-draft">Draft</span>'
        : '<span class="vbadge vbadge-none">Not built</span>')
    : '<span class="vbadge vbadge-none">No data</span>';

  return `
    <div class="vheader">
      <div class="vheader-title">
        <h1>${titles[state.view] || 'Scheduler'} ${statusBadge}</h1>
        <span class="vheader-date">${dateLong(day)}</span>
      </div>
      <div class="vheader-actions">
        <div class="vday-nav">
          <button class="vbtn-icon" data-action="prevDay" title="Previous day">&lsaquo;</button>
          <span class="vday-label">${dayName(day)} ${day.split('-')[2]}</span>
          <button class="vbtn-icon" data-action="nextDay" title="Next day">&rsaquo;</button>
        </div>
        ${state.view === 'scheduler' ? `
          <button class="vbtn vbtn-outline" data-action="generate">Generate</button>
          <button class="vbtn vbtn-primary" data-action="publish">Publish</button>
        ` : ''}
        ${state.view === 'sheet' ? '<button class="vbtn vbtn-outline" data-action="print">Print</button>' : ''}
      </div>
    </div>
  `;
}

function renderTabs(): string {
  const tabs = [
    { view: 'scheduler', label: 'Scheduler' },
    { view: 'rotation', label: 'Rotation' },
    { view: 'calloffs', label: 'Call-offs' },
    { view: 'details', label: 'Details' },
    { view: 'history', label: 'History' },
    { view: 'sheet', label: 'Full Schedule' },
  ];
  const isPhone = window.innerWidth < 691;
  let phonePanes = '';
  if (isPhone && state.view === 'scheduler') {
    const panes = [
      { id: 'roster', label: 'Roster' },
      { id: 'posts', label: 'Posts' },
      { id: 'selected', label: 'Selected' },
    ];
    phonePanes = `<div class="vpane-toggle">${panes.map(p =>
      `<button class="vpane-btn ${state.mobilePane === p.id ? 'active' : ''}" data-pane="${p.id}">${p.label}</button>`
    ).join('')}</div>`;
  }

  return `
    <div class="vtabs">${tabs.map(t =>
      `<button class="vtab ${state.view === t.view ? 'active' : ''}" data-view="${t.view}">${t.label}</button>`
    ).join('')}</div>
    ${phonePanes}
  `;
}

function renderView(): string {
  switch (state.view) {
    case 'scheduler': return renderScheduler();
    case 'rotation': return renderRotation();
    case 'history': return renderHistory();
    case 'details': return renderDetails();
    case 'calloffs': return renderCalloffs();
    case 'sheet': return renderSheet();
    default: return '';
  }
}

// ─── Render: Scheduler ──────────────────────────────────────────────────────

function renderScheduler(): string {
  const isPhone = window.innerWidth < 691;

  if (!dayData || !roster.length) {
    return '<div class="vplaceholder">No schedule data. Press Generate to create a schedule.</div>';
  }

  const rosterHtml = renderRosterPanel();
  const gridHtml = renderPostingGrid();
  const panelHtml = renderSelectedPanel();

  if (isPhone) {
    switch (state.mobilePane) {
      case 'roster': return rosterHtml;
      case 'posts': return gridHtml;
      case 'selected': return panelHtml;
    }
  }

  return `<div class="vscheduler-grid">${rosterHtml}${gridHtml}${panelHtml}</div>`;
}

function renderRosterPanel(): string {
  const filterShift = state.rosterTab;
  let officers = roster.filter((o: any) => o.shift === filterShift && o.active);
  if (state.searchQuery) {
    const q = state.searchQuery.toLowerCase();
    officers = officers.filter((o: any) => o.name.toLowerCase().includes(q));
  }

  const countByShift = {
    swing: roster.filter((o: any) => o.shift === 'swing' && o.active).length,
    day: roster.filter((o: any) => o.shift === 'day' && o.active).length,
    grave: roster.filter((o: any) => o.shift === 'grave' && o.active).length,
  };

  return `
    <div class="vroster">
      <div class="vroster-header">
        <h2>Roster</h2>
        <span class="vroster-count">${countByShift[filterShift]}</span>
      </div>
      <input type="text" class="vsearch" placeholder="Search officers\u2026" data-action="search" value="${esc(state.searchQuery)}">
      <div class="vroster-tabs">
        ${(['swing', 'day', 'grave'] as const).map(s =>
          `<button class="vroster-tab ${state.rosterTab === s ? 'active' : ''}" data-rostertab="${s}">${s.charAt(0).toUpperCase() + s.slice(1)} (${countByShift[s]})</button>`
        ).join('')}
      </div>
      <div class="vroster-list">
        ${officers.map((o: any) => {
          const g = officerGroup(o.id);
          const called = isCalledOff(o.id);
          const avatarClass = called ? 'called' : g >= 0 ? 'g' + g : 'unassigned';
          return `
            <button class="vroster-row ${state.selectedOfficer === o.id ? 'selected' : ''} ${called ? 'called' : ''}" data-officer="${o.id}">
              <div class="vofficer-avatar ${avatarClass}">${initials(o.name)}</div>
              <div class="vofficer-info">
                <span class="vofficer-name">${displayName(o.name)}</span>
                <span class="vofficer-role">${o.role === 'rw' ? 'Report Writer' : o.role === 'dispatch' ? 'Dispatcher' : o.is_fto ? 'FTO' : 'Officer'}${called ? ' \u2014 Called Off' : ''}</span>
              </div>
            </button>`;
        }).join('')}
      </div>
    </div>
  `;
}

function renderPostingGrid(): string {
  if (!parsed || parsed.generation === 0) {
    return `
      <div class="vgrid">
        <div class="vgrid-header"><h2>Posting Grid</h2></div>
        <div class="vgrid-content">
          <div class="vplaceholder">No schedule generated yet. Press Generate.</div>
        </div>
      </div>`;
  }

  const closures = closureMap();
  const blockLabels = BLOCKS.slice(0, NUM_BLOCKS);

  let groupsHtml = '';

  // Header row with time labels
  groupsHtml += `
    <div class="vblock-header-row">
      <div></div>
      ${blockLabels.map(b => `<div class="vblock-header-cell">${b}</div>`).join('')}
    </div>`;

  // Render groups 0-4 (officer posts) + group 5 (report writers)
  for (let gi = 0; gi <= RWG; gi++) {
    const group = GROUPS[gi];
    if (!group) continue;
    groupsHtml += `
      <div class="vgroup">
        <div class="vgroup-header g${gi}">
          ${esc(group.name)}
          <span class="vgroup-note">${esc(group.note)}</span>
        </div>`;

    for (let pi = 0; pi < group.posts.length; pi++) {
      const post = group.posts[pi];
      const sk = gi + ':' + pi;
      const closure = closures[sk];
      const pl = pLabel(sk);
      const isSelected = state.selectedPost === sk;

      let blockCells = '';
      for (let b = 0; b < NUM_BLOCKS; b++) {
        const occ = parsed!.occupancy[sk];
        const officerId = occ ? occ[b] : null;

        if (closure) {
          blockCells += `<div class="vblock-cell closed">${esc(closure.reason)}</div>`;
        } else if (officerId !== null) {
          const name = officerName(officerId);
          blockCells += `<div class="vblock-cell assigned" data-officer="${officerId}">${shortName(name)}</div>`;
        } else if (pl) {
          blockCells += `<div class="vblock-cell p-closed">${pl}</div>`;
        } else {
          blockCells += `<div class="vblock-cell empty">\u2014</div>`;
        }
      }

      groupsHtml += `
        <div class="vpost-row ${isSelected ? 'selected' : ''}" data-post="${sk}">
          <div class="vpost-label">
            ${pl ? `<span class="vp-label">${pl}</span>` : ''}
            ${esc(post.name)}
            ${post.shortName ? `<span class="vpost-short">${esc(post.shortName)}</span>` : ''}
          </div>
          ${blockCells}
        </div>`;
    }
    groupsHtml += '</div>';
  }

  // Dispatch section
  const dispatchRows = dayData.dispatch || [];
  groupsHtml += `
    <div class="vdispatch">
      <div class="vdispatch-header">CCI Dispatch</div>
      ${CCI_SEATS.map((seat, si) => {
        const ds = dispatchRows.find((d: any) => d.seat_index === si);
        const officerId = ds?.officer_id;
        const offline = ds?.is_offline;
        const coverOfficerId = ds?.cover_officer_id;
        let content = '';
        if (offline) {
          content = `<span class="vdispatch-offline">Offline</span>`;
          if (coverOfficerId) content += ` \u2014 Cover: ${displayName(officerName(coverOfficerId))}`;
        } else if (officerId) {
          content = `<span class="vdispatch-officer">${displayName(officerName(officerId))}</span>`;
        } else {
          content = `<span class="vdispatch-officer" style="color:var(--c-text-faint)">\u2014</span>`;
        }
        return `
          <div class="vdispatch-row" data-cci="${si}">
            <div class="vdispatch-seat">${esc(CCI_SHORT[si])}</div>
            <div>${content}</div>
          </div>`;
      }).join('')}
    </div>`;

  return `
    <div class="vgrid">
      <div class="vgrid-header">
        <h2>Posting Grid</h2>
        <span class="vbadge vbadge-count">Gen ${parsed.generation}</span>
      </div>
      <div class="vgrid-content">${groupsHtml}</div>
    </div>`;
}

function renderSelectedPanel(): string {
  // Officer selected
  if (state.selectedOfficer !== null) {
    const o = rosterById[state.selectedOfficer];
    if (!o) return renderEmptyPanel();
    const g = officerGroup(o.id);
    const avatarBg = g >= 0 ? GROUP_COLORS[g] : '#9CA3AF';
    const called = isCalledOff(o.id);

    // Get this officer's assignments per block
    let assignRows = '';
    if (parsed) {
      const blocks = parsed.assignments[String(o.id)];
      if (blocks && blocks.length) {
        assignRows = blocks.map((bl, b) => {
          if (bl[0] < 0) return '';
          const group = GROUPS[bl[0]];
          const post = group?.posts[bl[1]];
          if (!post) return '';
          return `
            <div class="vpanel-assign-row">
              <span class="vpanel-assign-time">${BLOCKS[b]}</span>
              <span class="vpanel-assign-post">${esc(post.name)}</span>
            </div>`;
        }).join('');
      }
    }

    // Check for OT
    const otEntries = (dayData?.overtime || []).filter((e: any) => e.officer_id === o.id);
    let otHtml = '';
    if (otEntries.length) {
      otHtml = `
        <div class="vpanel-section">
          <div class="vpanel-section-title">Overtime</div>
          ${otEntries.map((e: any) => `
            <div class="vpanel-assign-row" style="background:var(--c-ot-bg)">
              <span style="color:var(--c-ot-purple);font-weight:700;font-size:10.5px">${formatHalf(e.start_half)}\u2013${formatHalf(e.end_half)}</span>
              ${e.notes ? `<span style="color:var(--c-text-muted);font-size:11px">${esc(e.notes)}</span>` : ''}
            </div>`).join('')}
        </div>`;
    }

    return `
      <div class="vpanel">
        <div class="vpanel-header"><h2>Officer</h2></div>
        <div class="vpanel-content">
          <div class="vpanel-officer-header">
            <div class="vpanel-officer-avatar" style="background:${avatarBg}">${initials(o.name)}</div>
            <div>
              <div class="vpanel-officer-name">${displayName(o.name)}</div>
              <div class="vpanel-officer-role">${o.role === 'rw' ? 'Report Writer' : o.role === 'dispatch' ? 'Dispatcher' : o.is_fto ? 'FTO' : 'Officer'}${called ? ' \u2014 Called Off' : ''}</div>
            </div>
          </div>
          ${called ? '<div class="vbadge vbadge-danger vmb-8">Called Off</div>' : ''}
          <div class="vpanel-section">
            <div class="vpanel-section-title">Assignments</div>
            <div class="vpanel-assignments">${assignRows || '<div class="vtext-muted vtext-sm">Not assigned</div>'}</div>
          </div>
          ${otHtml}
          <div class="vpanel-action-row">
            <button class="vbtn vbtn-sm vbtn-danger" data-action="calloff" data-oid="${o.id}">Call Off</button>
          </div>
        </div>
      </div>`;
  }

  // Post selected
  if (state.selectedPost !== null) {
    const [gi, pi] = state.selectedPost.split(':').map(Number);
    const group = GROUPS[gi];
    const post = group?.posts[pi];
    if (!group || !post) return renderEmptyPanel();
    const sk = state.selectedPost;
    const closure = closureMap()[sk];
    const pl = pLabel(sk);

    let officerRows = '';
    if (parsed) {
      const occ = parsed.occupancy[sk];
      if (occ) {
        officerRows = occ.map((oid, b) => {
          if (oid === null) return `<div class="vpanel-assign-row"><span class="vpanel-assign-time">${BLOCKS[b]}</span><span class="vtext-muted">\u2014</span></div>`;
          return `
            <div class="vpanel-assign-row">
              <span class="vpanel-assign-time">${BLOCKS[b]}</span>
              <span class="vpanel-assign-post">${displayName(officerName(oid))}</span>
            </div>`;
        }).join('');
      }
    }

    return `
      <div class="vpanel">
        <div class="vpanel-header"><h2>Post</h2></div>
        <div class="vpanel-content">
          <div class="vpanel-post-name">${esc(post.name)}${post.shortName ? ` (${esc(post.shortName)})` : ''}</div>
          <div class="vpanel-post-group" style="color:${GROUP_COLORS[gi]}">${esc(group.name)} ${pl ? `\u00B7 ${pl}` : ''}</div>
          ${closure ? `<div class="vbadge vbadge-danger vmb-8">${esc(closure.reason)}</div>` : ''}
          ${post.held ? '<div class="vtext-sm vtext-muted vmb-8">Held all shift</div>' : '<div class="vtext-sm vtext-muted vmb-8">Rotates every 2 hours</div>'}
          <div class="vpanel-section">
            <div class="vpanel-section-title">Officers by Block</div>
            <div class="vpanel-assignments">${officerRows || '<div class="vtext-muted vtext-sm">No officers assigned</div>'}</div>
          </div>
          <div class="vpanel-action-row">
            ${!closure ? `<button class="vbtn vbtn-sm vbtn-danger" data-action="close-post" data-sk="${sk}">Close Post</button>` : ''}
          </div>
        </div>
      </div>`;
  }

  // CCI seat selected
  if (state.selectedCci !== null) {
    const si = state.selectedCci;
    const ds = (dayData?.dispatch || []).find((d: any) => d.seat_index === si);
    return `
      <div class="vpanel">
        <div class="vpanel-header"><h2>Dispatch Seat</h2></div>
        <div class="vpanel-content">
          <div class="vpanel-post-name">${esc(CCI_SEATS[si])}</div>
          <div class="vpanel-post-group" style="color:var(--g-dispatch)">CCI Dispatch</div>
          <div class="vpanel-section">
            <div class="vpanel-section-title">Assigned</div>
            <div class="vpanel-assign-row">
              ${ds?.officer_id ? displayName(officerName(ds.officer_id)) : '<span class="vtext-muted">\u2014</span>'}
            </div>
          </div>
          ${ds?.is_offline ? '<div class="vbadge vbadge-danger vmb-8">Offline</div>' : ''}
        </div>
      </div>`;
  }

  return renderEmptyPanel();
}

function renderEmptyPanel(): string {
  return `
    <div class="vpanel">
      <div class="vpanel-header"><h2>Selected</h2></div>
      <div class="vpanel-content">
        <div class="vplaceholder">Tap an officer or post to see details</div>
      </div>
    </div>`;
}

// ─── Render: Call-offs ──────────────────────────────────────────────────────

function renderCalloffs(): string {
  const calloffs = dayData?.calloffs || [];
  const swaps = dayData?.swaps || [];
  const overtime = dayData?.overtime || [];

  let calloffsList = '';
  if (calloffs.length === 0) {
    calloffsList = '<div class="vtext-muted vtext-sm" style="padding:12px">No call-offs today</div>';
  } else {
    calloffsList = calloffs.map((c: any) => {
      const name = displayName(officerName(c.officer_id));
      return `
        <div class="vcalloff-item">
          <div class="vofficer-avatar called">${initials(officerName(c.officer_id))}</div>
          <div class="vcalloff-info">
            <div class="vcalloff-name">${name}</div>
            <div class="vcalloff-desc">${c.covered_by ? 'Covered by ' + esc(c.covered_by) : 'Not covered'}</div>
          </div>
          <button class="vbtn vbtn-sm vbtn-outline" data-action="undo-calloff" data-cid="${c.id}">Undo</button>
        </div>`;
    }).join('');
  }

  let swapsList = '';
  if (swaps.length) {
    swapsList = `
      <div class="vcard vmb-8">
        <div class="vcard-header"><h2>Swaps</h2><span class="vbadge vbadge-count">${swaps.length}</span></div>
        <div class="vcard-body">
          ${swaps.map((s: any) => {
            const group = GROUPS[s.post_group];
            const post = group?.posts[s.post_index];
            return `
              <div class="vcalloff-item">
                <div class="vcalloff-info">
                  <div class="vcalloff-name">${post ? esc(post.name) : s.post_group + ':' + s.post_index}</div>
                  <div class="vcalloff-desc">${s.from_officer_id ? displayName(officerName(s.from_officer_id)) + ' \u2192 ' : ''}${displayName(officerName(s.to_officer_id))}</div>
                </div>
              </div>`;
          }).join('')}
        </div>
      </div>`;
  }

  let otList = '';
  if (overtime.length) {
    otList = `
      <div class="vcard">
        <div class="vcard-header"><h2>Overtime</h2><span class="vbadge vbadge-count">${overtime.length}</span></div>
        <div class="vcard-body">
          ${overtime.map((o: any) => `
            <div class="vcalloff-item" style="border-color:var(--c-ot-border)">
              <div class="vofficer-avatar" style="background:var(--c-ot-purple)">${initials(officerName(o.officer_id))}</div>
              <div class="vcalloff-info">
                <div class="vcalloff-name">${displayName(officerName(o.officer_id))}</div>
                <div class="vcalloff-desc">${formatHalf(o.start_half)}\u2013${formatHalf(o.end_half)}${o.notes ? ' \u00B7 ' + esc(o.notes) : ''}</div>
              </div>
            </div>`).join('')}
        </div>
      </div>`;
  }

  return `
    <div class="vcalloffs-layout">
      <div class="vcalloffs-main">
        <div class="vcard vmb-8">
          <div class="vcard-header">
            <h2>Call-offs</h2>
            <span class="vbadge vbadge-count">${calloffs.length}</span>
          </div>
          <div class="vcard-body">${calloffsList}</div>
        </div>
        ${swapsList}
        ${otList}
      </div>
      <div class="vcalloffs-sidebar">
        <div class="vcard">
          <div class="vcard-header"><h2>Quick Actions</h2></div>
          <div class="vcard-body">
            <button class="vbtn vbtn-danger vw-full vmb-8" data-action="add-calloff">Add Call-off</button>
            <button class="vbtn vbtn-outline vw-full vmb-8" data-action="add-swap">Add Swap</button>
            <button class="vbtn vbtn-outline vw-full" data-action="add-ot">Add Overtime</button>
          </div>
        </div>
      </div>
    </div>`;
}

// ─── Render: Details ────────────────────────────────────────────────────────

function renderDetails(): string {
  const events = globalData.events || [];
  const specials = globalData.specials || [];
  const rollNotes = globalData.rollNotes || [];
  const dispatch = dayData?.dispatch || [];

  const eventsHtml = events.length
    ? events.map((e: any) => `
        <div class="vdetail-item">
          <span style="color:${e.is_open ? 'var(--c-success)' : 'var(--c-text-muted)'};font-weight:700">${e.is_open ? '\u25CF' : '\u25CB'}</span>
          <span style="flex:1;font-weight:600">${esc(e.name)}</span>
          <span class="vbadge ${e.is_open ? 'vbadge-posted' : 'vbadge-none'}">${e.is_open ? 'Open' : 'Closed'}</span>
        </div>`).join('')
    : '<div class="vtext-muted vtext-sm">No events</div>';

  const specialsHtml = specials.length
    ? specials.map((s: any) => `
        <div class="vdetail-item">
          <span style="flex:1;font-weight:600">${esc(s.title)}</span>
          ${s.officer_id ? `<span class="vtext-sm vtext-muted">${displayName(officerName(s.officer_id))}</span>` : ''}
        </div>`).join('')
    : '<div class="vtext-muted vtext-sm">No special details</div>';

  const notesHtml = rollNotes.length
    ? rollNotes.map((n: any) => `
        <div class="vdetail-item">
          <span style="flex:1">${esc(n.note)}</span>
        </div>`).join('')
    : '<div class="vtext-muted vtext-sm">No roll call notes</div>';

  const dispatchHtml = CCI_SEATS.map((seat, si) => {
    const ds = dispatch.find((d: any) => d.seat_index === si);
    return `
      <div class="vdetail-item">
        <span style="font-weight:600;min-width:90px">${esc(CCI_SHORT[si])}</span>
        <span>${ds?.officer_id ? displayName(officerName(ds.officer_id)) : '\u2014'}</span>
        ${ds?.is_offline ? '<span class="vbadge vbadge-danger">Offline</span>' : ''}
      </div>`;
  }).join('');

  return `
    <div class="vdetails-grid">
      <div class="vdetail-section">
        <h3>Events</h3>
        ${eventsHtml}
        <button class="vbtn vbtn-sm vbtn-outline vmt-8" data-action="add-event">Add Event</button>
      </div>
      <div class="vdetail-section">
        <h3>CCI Dispatch</h3>
        ${dispatchHtml}
      </div>
      <div class="vdetail-section">
        <h3>Special Details</h3>
        ${specialsHtml}
        <button class="vbtn vbtn-sm vbtn-outline vmt-8" data-action="add-special">Add Special</button>
      </div>
      <div class="vdetail-section">
        <h3>Roll Call Notes</h3>
        ${notesHtml}
        <button class="vbtn vbtn-sm vbtn-outline vmt-8" data-action="add-note">Add Note</button>
      </div>
    </div>`;
}

// ─── Render: History ────────────────────────────────────────────────────────

function renderHistory(): string {
  const days: string[] = [];
  for (let i = 0; i < 14; i++) {
    days.push(addDays(state.currentDay, -i));
  }

  return `
    <div class="vhistory-grid">
      ${days.map(d => {
        const isCurrent = d === state.currentDay;
        return `
          <div class="vhistory-card ${isCurrent ? 'selected' : ''}" data-histday="${d}">
            <div class="vhistory-card-day">${dayName(d)} ${d.split('-')[2]}</div>
            <div class="vhistory-card-date">${d}</div>
            <div class="vhistory-card-stats">
              <span class="vbadge vbadge-none">Load to view</span>
            </div>
          </div>`;
      }).join('')}
    </div>
    <div class="vtext-muted vtext-sm vtext-center vmt-8">
      Tap a day to load its published snapshot. Navigate with the day arrows to view any day.
    </div>`;
}

// ─── Render: Rotation ───────────────────────────────────────────────────────

function renderRotation(): string {
  if (!parsed || parsed.generation === 0) {
    return '<div class="vplaceholder">Generate a schedule first to see rotation data.</div>';
  }

  const swingOfficers = roster.filter((o: any) => o.shift === 'swing' && o.active && o.role === 'officer');

  const rows: { name: string; id: number; counts: number[] }[] = [];
  for (const o of swingOfficers) {
    const blocks = parsed.assignments[String(o.id)];
    const counts = [0, 0, 0, 0, 0, 0];
    if (blocks) {
      for (const [g] of blocks) {
        if (g >= 0 && g < counts.length) counts[g] += 1;
      }
    }
    rows.push({ name: o.name, id: o.id, counts });
  }

  const groupNames = GROUPS.map(g => g.name);
  const maxCount = Math.max(1, ...rows.flatMap(r => r.counts));

  function heatClass(v: number): string {
    if (v === 0) return 'vheat-0';
    const ratio = v / maxCount;
    if (ratio < 0.25) return 'vheat-1';
    if (ratio < 0.5) return 'vheat-2';
    if (ratio < 0.75) return 'vheat-3';
    return 'vheat-4';
  }

  return `
    <div class="vcard">
      <div class="vcard-header">
        <h2>Rotation Fairness \u2014 ${state.currentDay}</h2>
        <span class="vtext-sm vtext-muted">Blocks per group (today)</span>
      </div>
      <div class="vcard-body" style="overflow-x:auto">
        <div class="vfairness-grid">
          <div class="vfairness-header">Officer</div>
          ${groupNames.slice(0, 5).map((n, i) => `<div class="vfairness-header" style="color:${GROUP_COLORS[i]}">${esc(n)}</div>`).join('')}
          ${rows.map(r => `
            <div class="vfairness-name">
              <div class="vofficer-avatar g${officerGroup(r.id)}" style="width:22px;height:22px;font-size:9px;flex:0 0 22px">${initials(r.name)}</div>
              ${shortName(r.name)}
            </div>
            ${r.counts.slice(0, 5).map(c => `<div class="vheat-cell ${heatClass(c)}">${c}</div>`).join('')}
          `).join('')}
        </div>
      </div>
    </div>`;
}

// ─── Render: Sheet (Print View) ─────────────────────────────────────────────

function renderSheet(): string {
  if (!parsed || parsed.generation === 0) {
    return '<div class="vplaceholder">No schedule to display. Generate first.</div>';
  }

  const blockLabels = BLOCKS.slice(0, NUM_BLOCKS);

  let tableRows = '';
  for (let gi = 0; gi <= RWG; gi++) {
    const group = GROUPS[gi];
    if (!group) continue;

    tableRows += `<tr><td colspan="${NUM_BLOCKS + 1}" style="background:${GROUP_COLORS[gi]};color:#FFF;font-weight:700;padding:6px 8px">${esc(group.name)}</td></tr>`;

    for (let pi = 0; pi < group.posts.length; pi++) {
      const post = group.posts[pi];
      const sk = gi + ':' + pi;
      const occ = parsed!.occupancy[sk];

      tableRows += `<tr>
        <td style="font-weight:600">${esc(post.name)}</td>
        ${blockLabels.map((_, b) => {
          const oid = occ ? occ[b] : null;
          return `<td>${oid !== null ? displayName(officerName(oid)) : '\u2014'}</td>`;
        }).join('')}
      </tr>`;
    }
  }

  // Dispatch
  const dispatch = dayData?.dispatch || [];
  tableRows += `<tr><td colspan="${NUM_BLOCKS + 1}" style="background:var(--c-dark);color:#FFF;font-weight:700;padding:6px 8px">CCI Dispatch</td></tr>`;
  CCI_SEATS.forEach((seat, si) => {
    const ds = dispatch.find((d: any) => d.seat_index === si);
    tableRows += `<tr>
      <td style="font-weight:600">${esc(CCI_SHORT[si])}</td>
      <td colspan="${NUM_BLOCKS}">${ds?.officer_id ? displayName(officerName(ds.officer_id)) : '\u2014'}${ds?.is_offline ? ' (Offline)' : ''}</td>
    </tr>`;
  });

  return `
    <div class="vsheet">
      <h2>Aria Security \u2014 Swing Shift Posting</h2>
      <div style="text-align:center;margin-bottom:12px">
        <span style="font-weight:600">${dateLong(state.currentDay)}</span>
        ${parsed.posted ? '<span class="vbadge vbadge-posted" style="margin-left:8px">Published</span>' : '<span class="vbadge vbadge-draft" style="margin-left:8px">Draft</span>'}
      </div>
      <table class="vsheet-table">
        <thead>
          <tr>
            <th>Post</th>
            ${blockLabels.map(b => `<th>${b}</th>`).join('')}
          </tr>
        </thead>
        <tbody>${tableRows}</tbody>
      </table>
    </div>`;
}

// ─── Event Binding ──────────────────────────────────────────────────────────

function bindEvents() {
  // View tabs + side menu
  app.querySelectorAll('[data-view]').forEach(el => {
    el.addEventListener('click', (e) => {
      e.preventDefault();
      setView((el as HTMLElement).dataset.view as any);
    });
  });

  // Day nav
  app.querySelector('[data-action="prevDay"]')?.addEventListener('click', prevDay);
  app.querySelector('[data-action="nextDay"]')?.addEventListener('click', nextDay);

  // Mobile pane toggle
  app.querySelectorAll('[data-pane]').forEach(el => {
    el.addEventListener('click', () => setMobilePane((el as HTMLElement).dataset.pane as any));
  });

  // Officer selection (roster + grid cells)
  app.querySelectorAll('[data-officer]').forEach(el => {
    el.addEventListener('click', (e) => {
      e.stopPropagation();
      state.selectedOfficer = parseInt((el as HTMLElement).dataset.officer!, 10);
      state.selectedPost = null;
      state.selectedCci = null;
      if (window.innerWidth < 691) state.mobilePane = 'selected';
      render();
    });
  });

  // Post selection
  app.querySelectorAll('[data-post]').forEach(el => {
    el.addEventListener('click', () => {
      state.selectedPost = (el as HTMLElement).dataset.post!;
      state.selectedOfficer = null;
      state.selectedCci = null;
      if (window.innerWidth < 691) state.mobilePane = 'selected';
      render();
    });
  });

  // CCI seat selection
  app.querySelectorAll('[data-cci]').forEach(el => {
    el.addEventListener('click', () => {
      state.selectedCci = parseInt((el as HTMLElement).dataset.cci!, 10);
      state.selectedOfficer = null;
      state.selectedPost = null;
      if (window.innerWidth < 691) state.mobilePane = 'selected';
      render();
    });
  });

  // Roster tab
  app.querySelectorAll('[data-rostertab]').forEach(el => {
    el.addEventListener('click', () => {
      state.rosterTab = (el as HTMLElement).dataset.rostertab as any;
      render();
    });
  });

  // Search
  const searchEl = app.querySelector('[data-action="search"]') as HTMLInputElement;
  if (searchEl) {
    searchEl.addEventListener('input', () => {
      state.searchQuery = searchEl.value;
      render();
      const newSearch = app.querySelector('[data-action="search"]') as HTMLInputElement;
      if (newSearch) { newSearch.focus(); newSearch.selectionStart = newSearch.selectionEnd = newSearch.value.length; }
    });
  }

  // History day card
  app.querySelectorAll('[data-histday]').forEach(el => {
    el.addEventListener('click', () => {
      goDay((el as HTMLElement).dataset.histday!);
      setView('scheduler');
    });
  });

  // Generate
  app.querySelector('[data-action="generate"]')?.addEventListener('click', async () => {
    showToast('Generating schedule\u2026');
    try {
      await api.post('schedule', {
        day: state.currentDay,
        generation: (parsed?.generation || 0) + 1,
        assignments: '{}',
        version: state.dayVersion,
      });
      await loadDay(state.currentDay);
      showToast('Schedule generated');
    } catch (e: any) {
      if (e.message !== 'stale') showToast('Generation failed');
    }
  });

  // Publish
  app.querySelector('[data-action="publish"]')?.addEventListener('click', async () => {
    try {
      await api.post('publish', {
        day: state.currentDay,
        version: state.dayVersion,
      });
      await loadDay(state.currentDay);
      showToast('Published');
    } catch (e: any) {
      if (e.message !== 'stale') showToast('Publish failed');
    }
  });

  // Print
  app.querySelector('[data-action="print"]')?.addEventListener('click', () => window.print());

  // Call-off action from panel
  app.querySelector('[data-action="calloff"]')?.addEventListener('click', async () => {
    const oid = parseInt(app.querySelector('[data-action="calloff"]')?.getAttribute('data-oid') || '0', 10);
    if (!oid) return;
    try {
      await api.post('calloffs', { day: state.currentDay, officer_id: oid, version: state.dayVersion });
      await loadDay(state.currentDay);
      showToast(displayName(officerName(oid)) + ' called off');
    } catch (e: any) {
      if (e.message !== 'stale') showToast('Call-off failed');
    }
  });

  // Undo call-off
  app.querySelectorAll('[data-action="undo-calloff"]').forEach(el => {
    el.addEventListener('click', async () => {
      const cid = parseInt((el as HTMLElement).dataset.cid!, 10);
      try {
        await api.del('calloffs', { id: cid, day: state.currentDay, version: state.dayVersion });
        await loadDay(state.currentDay);
        showToast('Call-off undone');
      } catch (e: any) {
        if (e.message !== 'stale') showToast('Undo failed');
      }
    });
  });

  // Close post
  app.querySelector('[data-action="close-post"]')?.addEventListener('click', async () => {
    const sk = app.querySelector('[data-action="close-post"]')?.getAttribute('data-sk');
    if (!sk) return;
    const [g, p] = sk.split(':').map(Number);
    try {
      await api.post('closures', { day: state.currentDay, post_group: g, post_index: p, reason: 'Closed by manager', version: state.dayVersion });
      await loadDay(state.currentDay);
      showToast('Post closed');
    } catch (e: any) {
      if (e.message !== 'stale') showToast('Close failed');
    }
  });

  // Add call-off (from calloffs view quick action)
  app.querySelector('[data-action="add-calloff"]')?.addEventListener('click', () => {
    const name = prompt('Officer name (Last, First):');
    if (!name) return;
    const officer = roster.find((o: any) => o.name.toLowerCase() === name.toLowerCase());
    if (!officer) { showToast('Officer not found'); return; }
    api.post('calloffs', { day: state.currentDay, officer_id: officer.id, version: state.dayVersion })
      .then(() => loadDay(state.currentDay).then(() => showToast('Call-off added')))
      .catch((e: any) => { if (e.message !== 'stale') showToast('Failed'); });
  });

  // Add event
  app.querySelector('[data-action="add-event"]')?.addEventListener('click', () => {
    const name = prompt('Event name:');
    if (!name) return;
    api.post('details', { type: 'event', name, is_open: true })
      .then(() => loadGlobalData().then(() => render()))
      .catch(() => showToast('Failed to add event'));
  });

  // Add special
  app.querySelector('[data-action="add-special"]')?.addEventListener('click', () => {
    const title = prompt('Special detail title:');
    if (!title) return;
    api.post('details', { type: 'special', title })
      .then(() => loadGlobalData().then(() => render()))
      .catch(() => showToast('Failed to add special'));
  });

  // Add roll note
  app.querySelector('[data-action="add-note"]')?.addEventListener('click', () => {
    const note = prompt('Roll call note:');
    if (!note) return;
    api.post('details', { type: 'roll_note', day: state.currentDay, note })
      .then(() => loadGlobalData().then(() => render()))
      .catch(() => showToast('Failed to add note'));
  });
}

// ─── Init ───────────────────────────────────────────────────────────────────

async function init() {
  await Promise.all([loadDay(state.currentDay), loadGlobalData()]);
  startPoll();
  window.addEventListener('resize', render);
}

init();
